import React, { useMemo, useState } from 'react';
import {
	Autocomplete,
	Box,
	Button,
	Checkbox,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	List,
	ListItemButton,
	Snackbar,
	Stack,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

type WorkStatus = 'Completed' | 'In progress';

type WorkItem = {
	id: number;
	selected: boolean;
	title: string;
	workers: string[];
	project: string;
	workType: string;
	quantity: string;
	unit: string;
	location: string;
	status: WorkStatus;
	isNew?: boolean;
};

const workerOptions = [
	'Abdullah',
	'Akram Khan',
	'Atik Dewan',
	'Eyasin',
	'Faruk Hossain',
	'Miah Md Kazim',
	'Miah Sipon',
	'Rahman Atiqur',
	'Sujan Sorker',
	'Sumon',
	'Unidentified WhatsApp Worker (80464681)',
	'Unidentified WhatsApp Worker (80646271)',
	'Unidentified WhatsApp Worker (81201101)',
	'Unidentified WhatsApp Worker (81875452)',
	'Unidentified WhatsApp Worker (85961354)',
	'Unidentified WhatsApp Worker (86616318)',
	'Unidentified WhatsApp Worker (89364252)',
	'Unidentified WhatsApp Worker (89792073)',
	'Unidentified WhatsApp Worker (98908074)',
];
const projectOptions = ['Tengah C5', 'Sprucing C5', 'G33C', 'G34H'];
const workTypeOptions = ['Window Installation', 'Casting Window', 'Bracket Installation', 'Sealant Work'];
const unitOptions = ['units', 'pieces', 'windows', 'hours'];

const initialItems: WorkItem[] = [
	{
		id: 1,
		selected: true,
		title: 'Window Installation',
		workers: ['Sumon'],
		project: 'Tengah C5',
		workType: 'Window Installation',
		quantity: '5',
		unit: 'units',
		location: 'Block 106A · Level 06',
		status: 'Completed',
	},
	{
		id: 2,
		selected: true,
		title: 'Casting Window',
		workers: ['Sumon'],
		project: 'Tengah C5',
		workType: 'Casting Window',
		quantity: '1',
		unit: 'pieces',
		location: 'Block 108A · Level 08',
		status: 'Completed',
	},
	{
		id: 3,
		selected: false,
		title: 'Bracket Installation',
		workers: ['Sumon'],
		project: 'Tengah C5',
		workType: 'Bracket Installation',
		quantity: '22',
		unit: 'windows',
		location: '',
		status: 'Completed',
	},
];

const fieldSx = {
	'& .MuiOutlinedInput-root': {
		minHeight: 40,
		borderRadius: 2.25,
		backgroundColor: '#f8fafc',
		transition: 'background-color 150ms ease, box-shadow 150ms ease',
		'& fieldset': { borderColor: '#dbe2ea' },
		'&:hover': {
			backgroundColor: '#fff',
			'& fieldset': { borderColor: '#aab7c5' },
		},
		'&.Mui-focused': {
			backgroundColor: '#fff',
			boxShadow: '0 0 0 3px rgba(45, 112, 203, 0.10)',
		},
	},
	'& .MuiOutlinedInput-input': { py: 0.95 },
	'& .MuiAutocomplete-inputRoot': { py: '1px !important' },
	'& .MuiFormHelperText-root': { mt: 0.35 },
};

const FieldLabel = ({ children }: { children: React.ReactNode }) => (
	<Typography
		component='label'
		variant='caption'
		sx={{ display: 'block', mb: 0.35, color: '#4b5b6b', fontWeight: 650 }}>
		{children}
	</Typography>
);

const isComplete = (item: WorkItem) =>
	Boolean(
		item.workers.length &&
			item.project &&
			item.workType &&
			Number(item.quantity) > 0 &&
			item.unit &&
			item.location.trim()
	);

export const ReviewDialogDesignPreview = () => {
	const [items, setItems] = useState<WorkItem[]>(initialItems);
	const [activeId, setActiveId] = useState(1);
	const [notice, setNotice] = useState('');

	const activeIndex = Math.max(
		0,
		items.findIndex((item) => item.id === activeId)
	);
	const activeItem = items[activeIndex];
	const selectedCount = useMemo(() => items.filter((item) => item.selected).length, [items]);

	const updateItem = (id: number, updates: Partial<WorkItem>) => {
		setItems((current) =>
			current.map((item) => {
				if (item.id !== id) return item;
				const next = { ...item, ...updates };
				if (updates.workType && item.title === item.workType) {
					next.title = updates.workType;
				}
				return next;
			})
		);
	};

	const addItem = () => {
		const nextId = Math.max(...items.map((item) => item.id)) + 1;
		setItems((current) => [
			...current,
			{
				id: nextId,
				selected: true,
				title: 'New work item',
				workers: ['Sumon'],
				project: 'Tengah C5',
				workType: '',
				quantity: '',
				unit: 'units',
				location: '',
				status: 'Completed',
				isNew: true,
			},
		]);
		setActiveId(nextId);
	};

	const removeAddedItem = (id: number) => {
		const removingIndex = items.findIndex((item) => item.id === id);
		const remaining = items.filter((item) => item.id !== id);
		setItems(remaining);
		if (activeId === id) {
			const nextIndex = Math.max(0, removingIndex - 1);
			setActiveId(remaining[nextIndex].id);
		}
	};

	const move = (offset: number) => {
		const next = Math.min(Math.max(activeIndex + offset, 0), items.length - 1);
		setActiveId(items[next].id);
	};

	return (
		<Box sx={{ minHeight: '100vh', bgcolor: '#eef2f6' }}>
			<Dialog
				open
				fullScreen
				PaperProps={{
					sx: {
						bgcolor: '#f7f9fb',
						backgroundImage: 'none',
					},
				}}>
				<DialogTitle
					sx={{
						minHeight: 60,
						px: { xs: 2, md: 3 },
						py: 1,
						bgcolor: '#fff',
						borderBottom: '1px solid #e7ebef',
						display: 'flex',
						alignItems: 'center',
						gap: 2,
					}}>
					<Box sx={{ flex: 1, minWidth: 0 }}>
						<Typography variant='h6' sx={{ fontWeight: 700, lineHeight: 1.25 }}>
							Review work
						</Typography>
						<Typography variant='body2' color='text.secondary' noWrap>
							28 Aug 2026, 8:09 PM · +91 8128 ••• 841
						</Typography>
					</Box>
					<Tooltip title='1 attachment'>
						<IconButton onClick={() => setNotice('Attachment preview opened')}>
							<AttachFileIcon />
						</IconButton>
					</Tooltip>
					<IconButton aria-label='Close preview' onClick={() => setNotice('Preview only')}>
						<CloseIcon />
					</IconButton>
				</DialogTitle>

				<DialogContent sx={{ p: 0, display: 'flex', minHeight: 0, overflow: 'hidden' }}>
					<Box
						component='aside'
						sx={{
							width: { xs: 210, md: 250 },
							flexShrink: 0,
							bgcolor: '#fff',
							borderRight: '1px solid #e7ebef',
							p: 1.5,
							overflowY: 'auto',
						}}>
						<Typography variant='subtitle2' sx={{ fontWeight: 700, px: 0.75 }}>
							{items.length} work items
						</Typography>
						<Typography variant='caption' color='text.secondary' sx={{ px: 0.75 }}>
							Select what should be recorded
						</Typography>

						<List disablePadding sx={{ mt: 1.5 }}>
							{items.map((item) => (
								<Stack key={item.id} direction='row' alignItems='center' sx={{ mb: 0.75 }}>
									<ListItemButton
										selected={item.id === activeId}
										onClick={() => setActiveId(item.id)}
										sx={{
											p: 1,
											minWidth: 0,
											borderRadius: 2,
											alignItems: 'flex-start',
											border: '1px solid',
											borderColor: item.id === activeId ? '#a9c7ee' : 'transparent',
											'&.Mui-selected': { bgcolor: '#eef5ff' },
											'&.Mui-selected:hover': { bgcolor: '#e7f1ff' },
										}}>
										<Checkbox
											size='small'
											checked={item.selected}
											onClick={(event) => event.stopPropagation()}
											onChange={(event) => updateItem(item.id, { selected: event.target.checked })}
											inputProps={{ 'aria-label': `Select ${item.title}` }}
											sx={{ p: 0.25, mr: 0.75, mt: -0.1 }}
										/>
										<Box sx={{ minWidth: 0, flex: 1 }}>
											<Typography variant='body2' sx={{ fontWeight: 650, lineHeight: 1.3 }} noWrap>
												{item.title}
											</Typography>
											<Typography
												variant='caption'
												sx={{ color: item.location ? 'text.secondary' : 'error.main' }}
												noWrap>
												{item.location ? `${item.quantity || '—'} ${item.unit}` : 'Location needed'}
											</Typography>
										</Box>
									</ListItemButton>
									{item.isNew && (
										<Tooltip title='Remove added item'>
											<IconButton
												size='small'
												aria-label={`Remove ${item.title}`}
												onClick={() => removeAddedItem(item.id)}
												sx={{ ml: 0.25, color: '#64748b' }}>
												<DeleteOutlineIcon fontSize='small' />
											</IconButton>
										</Tooltip>
									)}
								</Stack>
							))}
						</List>

						<Button
							fullWidth
							size='small'
							startIcon={<AddIcon />}
							onClick={addItem}
							sx={{
								mt: 0.5,
								py: 0.9,
								border: '1px dashed #b7c1cc',
								borderRadius: 2,
								color: '#334155',
								textTransform: 'none',
							}}>
							Add work item
						</Button>
					</Box>

					<Box
						component='main'
						sx={{
							flex: 1,
							minWidth: 0,
							overflowY: { xs: 'auto', md: 'hidden' },
							px: { xs: 2, md: 4 },
							py: { xs: 2, md: 2 },
						}}>
						<Box sx={{ width: '100%', maxWidth: 920, mx: 'auto' }}>
							<Box
								sx={{
									bgcolor: '#edf5ff',
									border: '1px solid #d8e8fb',
									borderRadius: 2.5,
									px: 1.75,
									py: 1,
									mb: 1.5,
								}}>
								<Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.25, sm: 1 }} sx={{ mb: 0.25 }}>
									<Typography variant='caption' sx={{ fontWeight: 700, color: '#31557f' }}>
										Original message
									</Typography>
									<Typography variant='caption' color='text.secondary'>
										Prerak P Trivedi · reporting for Sumon
									</Typography>
								</Stack>
								<Typography
									variant='body2'
									sx={{
										color: '#24364b',
										lineHeight: 1.45,
										display: '-webkit-box',
										WebkitLineClamp: 2,
										WebkitBoxOrient: 'vertical',
										overflow: 'hidden',
									}}>
									Tengah C5 window install Blk 106A level 06 - 5 unit install · Blk 108A level 08 casting window install
									1 pcs · Bracket install 22 window · Worker: Sumon
								</Typography>
							</Box>

							<Stack direction='row' alignItems='center' justifyContent='space-between' spacing={2} sx={{ mb: 1.5 }}>
								<Box>
									<Typography variant='caption' sx={{ color: '#315f96', fontWeight: 700 }}>
										AI interpreted this as
									</Typography>
									<Typography variant='h6' sx={{ fontWeight: 700 }}>
										{activeItem.title}
									</Typography>
									<Typography variant='caption' color='text.secondary'>
										Work item {activeIndex + 1} of {items.length}
									</Typography>
								</Box>
								<Stack direction='row' alignItems='center' spacing={0.75}>
									<Tooltip title='Previous item'>
										<span>
											<IconButton
												size='small'
												disabled={activeIndex === 0}
												onClick={() => move(-1)}
												aria-label='Previous item'>
												<ArrowBackIcon fontSize='small' />
											</IconButton>
										</span>
									</Tooltip>
									<Chip
										size='small'
										variant='outlined'
										color={isComplete(activeItem) ? 'success' : 'error'}
										label={isComplete(activeItem) ? 'Ready to record' : 'Needs attention'}
										sx={{
											bgcolor: isComplete(activeItem) ? '#f1fbf5' : '#fff6f5',
											fontWeight: 650,
										}}
									/>
									<Tooltip title='Next item'>
										<span>
											<IconButton
												size='small'
												disabled={activeIndex === items.length - 1}
												onClick={() => move(1)}
												aria-label='Next item'>
												<ArrowForwardIcon fontSize='small' />
											</IconButton>
										</span>
									</Tooltip>
								</Stack>
							</Stack>

							<Box
								sx={{
									bgcolor: '#fff',
									border: '1px solid #e4e9ef',
									borderRadius: 3,
									p: 1.5,
									display: 'grid',
									gridTemplateColumns: { xs: '1fr', md: 'repeat(6, minmax(0, 1fr))' },
									columnGap: 1.5,
									rowGap: 1.1,
								}}>
								<Box sx={{ gridColumn: { md: 'span 3' } }}>
									<FieldLabel>Workers</FieldLabel>
									<Autocomplete
										multiple
										filterSelectedOptions
										limitTags={2}
										size='small'
										options={workerOptions}
										value={activeItem.workers}
										onChange={(_, value) => updateItem(activeItem.id, { workers: value })}
										noOptionsText='No active workers found'
										ListboxProps={{ style: { maxHeight: 280 } }}
										renderInput={(params) => <TextField {...params} placeholder='Search workers' sx={fieldSx} />}
									/>
								</Box>
								<Box sx={{ gridColumn: { md: 'span 3' } }}>
									<FieldLabel>Project</FieldLabel>
									<Autocomplete
										disableClearable
										size='small'
										options={projectOptions}
										value={activeItem.project}
										onChange={(_, value) => updateItem(activeItem.id, { project: value })}
										renderInput={(params) => <TextField {...params} placeholder='Select project' sx={fieldSx} />}
									/>
								</Box>
								<Box sx={{ gridColumn: { md: 'span 3' } }}>
									<FieldLabel>Work type</FieldLabel>
									<Autocomplete
										size='small'
										options={workTypeOptions}
										value={activeItem.workType || null}
										onChange={(_, value) => updateItem(activeItem.id, { workType: value || '' })}
										renderInput={(params) => (
											<TextField {...params} placeholder='Select work type' error={!activeItem.workType} sx={fieldSx} />
										)}
									/>
								</Box>
								<Box sx={{ gridColumn: { md: 'span 1' } }}>
									<FieldLabel>Quantity</FieldLabel>
									<TextField
										fullWidth
										size='small'
										type='number'
										placeholder='0'
										value={activeItem.quantity}
										onChange={(event) => updateItem(activeItem.id, { quantity: event.target.value })}
										inputProps={{ min: 0 }}
										sx={fieldSx}
									/>
								</Box>
								<Box sx={{ gridColumn: { md: 'span 1' } }}>
									<FieldLabel>Unit</FieldLabel>
									<Autocomplete
										disableClearable
										size='small'
										options={unitOptions}
										value={activeItem.unit}
										onChange={(_, value) => updateItem(activeItem.id, { unit: value })}
										renderInput={(params) => <TextField {...params} sx={fieldSx} />}
									/>
								</Box>
								<Box sx={{ gridColumn: { md: 'span 1' } }}>
									<FieldLabel>Status</FieldLabel>
									<Autocomplete
										disableClearable
										size='small'
										options={['Completed', 'In progress'] as WorkStatus[]}
										value={activeItem.status}
										onChange={(_, value) => updateItem(activeItem.id, { status: value })}
										renderInput={(params) => <TextField {...params} sx={fieldSx} />}
									/>
								</Box>
								<Box sx={{ gridColumn: { md: '1 / -1' } }}>
									<FieldLabel>Location</FieldLabel>
									<TextField
										fullWidth
										size='small'
										placeholder='Block and level'
										value={activeItem.location}
										onChange={(event) => updateItem(activeItem.id, { location: event.target.value })}
										error={!activeItem.location.trim()}
										helperText={!activeItem.location.trim() ? 'Add a location to record this item' : undefined}
										sx={fieldSx}
									/>
								</Box>
							</Box>
						</Box>
					</Box>
				</DialogContent>

				<DialogActions
					sx={{
						minHeight: 60,
						px: { xs: 2, md: 3 },
						bgcolor: '#fff',
						borderTop: '1px solid #e7ebef',
					}}>
					<Typography variant='body2' color='text.secondary' sx={{ mr: 'auto' }}>
						{selectedCount} selected
					</Typography>
					<Button color='inherit' onClick={() => setNotice('Preview only')}>
						Cancel
					</Button>
					<Button
						variant='contained'
						disableElevation
						disabled={!selectedCount}
						onClick={() => setNotice(`${selectedCount} Work Done records are ready`)}
						sx={{ borderRadius: 2, px: 2.5, textTransform: 'none', fontWeight: 700 }}>
						Create {selectedCount} Work Done {selectedCount === 1 ? 'record' : 'records'}
					</Button>
				</DialogActions>
			</Dialog>
			<Snackbar open={Boolean(notice)} autoHideDuration={2200} message={notice} onClose={() => setNotice('')} />
		</Box>
	);
};
